import React, { useMemo } from "react";
import { Briefcase, Plus, Layers, Pencil, Trash2, Sparkles, ChevronDown, LayoutDashboard } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel, SelectSeparator,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const PortfolioSelectorBar = ({
  portfolios,
  activeId,
  onChange,
  onNew,
  onEdit,
  onDelete,
  onViewSuggestion,
  onViewDashboard,
  view,
  canEdit,
}) => {
  const isAll = activeId === "all";
  const active = useMemo(() => portfolios.find((p) => p.portfolio_id === activeId), [portfolios, activeId]);

  return (
    <Card className="border bg-card" data-testid="portfolio-selector-bar">
      <div className="p-3 sm:p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Briefcase className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Portfolio</span>
        </div>

        <div className="flex-1 min-w-[260px]">
          <Select value={activeId || ""} onValueChange={onChange}>
            <SelectTrigger
              className="h-11 font-display text-base font-semibold"
              data-testid="portfolio-selector"
            >
              <SelectValue placeholder="Select a portfolio" />
            </SelectTrigger>
            <SelectContent>
              {portfolios.length > 1 && (
                <>
                  <SelectGroup>
                    <SelectItem value="all" data-testid="portfolio-option-all">
                      <span className="flex items-center gap-2">
                        <Layers className="h-4 w-4" /> All Portfolios <span className="text-xs text-muted-foreground">(combined)</span>
                      </span>
                    </SelectItem>
                  </SelectGroup>
                  <SelectSeparator />
                </>
              )}
              <SelectGroup>
                <SelectLabel>Your portfolios</SelectLabel>
                {portfolios.map((p) => (
                  <SelectItem key={p.portfolio_id} value={p.portfolio_id} data-testid={`portfolio-option-${p.portfolio_id}`}>
                    <span className="flex items-center gap-2">
                      <Briefcase className="h-3.5 w-3.5" />
                      {p.portfolio_name}
                      {p.goal && (
                        <span className="text-xs text-muted-foreground">· {p.goal}</span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={onNew} className="gap-2" data-testid="new-portfolio-button">
            <Plus className="h-4 w-4" /> New portfolio
          </Button>
          {canEdit && (
            <>
              {view !== "suggestion" && active?.ai_suggestion && (
                <Button variant="outline" size="sm" onClick={onViewSuggestion} className="gap-2" data-testid="view-suggestion-button">
                  <Sparkles className="h-3.5 w-3.5" /> AI suggestion
                </Button>
              )}
              {view !== "dashboard" && (
                <Button variant="outline" size="sm" onClick={onViewDashboard} className="gap-2" data-testid="view-dashboard-button">
                  <LayoutDashboard className="h-3.5 w-3.5" /> Dashboard
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={onEdit} className="gap-2" data-testid="edit-portfolio-button">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-2"
                onClick={onDelete}
                data-testid="delete-portfolio-button"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </Button>
            </>
          )}
        </div>
      </div>
      {!isAll && active && (
        <div className="px-4 pb-3 sm:pb-4 -mt-1 flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
          {active.goal && <Badge variant="secondary">{active.goal}</Badge>}
          {active.risk_level && <Badge variant="outline">{active.risk_level}</Badge>}
          {active.holding_period && <Badge variant="outline">{active.holding_period}</Badge>}
          {active.sectors_to_avoid?.length > 0 && (
            <span>· Avoiding: {active.sectors_to_avoid.join(", ")}</span>
          )}
        </div>
      )}
    </Card>
  );
};

export default PortfolioSelectorBar;
